import { useCallback, useMemo } from 'react';
import { useCursorPagination } from '@frameio/graphql-client';
import { useGetProjectsInWorkspaceQuery } from '../../shared/gql/generated/GetProjectsInWorkspace';

type UseGetProjectsForWorkspaceNavOptions = {
  workspaceId: string;
  pageSize?: number;
  skip?: boolean;
};

export const DEFAULT_PAGE_SIZE = 5;

/**
 * Hook for fetching projects in navigation context with fixed sorting by name (ASC)
 * and pagination support. This hook does not use the global sort/filter context.
 */
export function useGetProjectsForWorkspaceNav({
  workspaceId,
  pageSize = DEFAULT_PAGE_SIZE,
  skip = false,
}: UseGetProjectsForWorkspaceNavOptions) {
  const { data, loading, error, fetchNext, previousData } = useCursorPagination(
    useGetProjectsInWorkspaceQuery({
      fetchPolicy: 'network-only',
      skip,
      variables: {
        workspaceId,
        page: {
          first: pageSize,
        },
        //Pass sortBy argument at a later stage
        orders: [
          {
            field: 'NAME',
            direction: 'ASC',
          },
        ],
      },
    })
  );

  const projects = data?.account?.workspace?.projects?.nodes;
  const previousProjects = previousData?.account?.workspace?.projects?.nodes;

  const projectIds = useMemo(() => {
    if (loading && previousProjects) {
      return previousProjects.map(({ id }) => id);
    }

    return (projects ?? []).map(({ id }) => id);
  }, [loading, previousProjects, projects]);

  const pageInfo = data?.account?.workspace?.projects?.pageInfo;

  const fetchMore = useCallback(() => {
    if (pageInfo) {
      fetchNext({ count: pageSize, pageInfo });
    }
  }, [fetchNext, pageInfo, pageSize]);

  const hasNextPage = pageInfo?.hasNextPage;
  const totalCount = data?.account?.workspace?.projects?.totalCount;

  return {
    projectIds,
    loading: !data && !previousData && loading,
    error,
    fetchMore,
    hasNextPage,
    totalCount,
  };
}
